(() => {
  const CART_DISCOUNT_CODE_KEY = 'cart-discount:code';
  const CACHED_PERCENT_KEY = 'welcome-discount:percent';
  const CACHED_CODE_KEY = 'welcome-discount:code';
  const CODE_CHANGED_EVENT = 'welcome-discount:code-changed';
  const WELCOME_CLAIMED_KEY = 'lf_welcome_claimed';
  const WELCOME_CLAIMED_EVENT = 'welcome-discount:claimed';
  const KLAVIYO_FORM_IDS = Object.freeze({
    'varmbakken.shop': 'Yj3Vt4',
    'lelafine.nl': 'YrGt3q',
    'lelafine.de': 'TSv7Mm',
    'lelafine.no': 'Srvbjm',
    'lelafine.be': 'S9wVut',
    'lelafine.se': 'YiLWGf',
  });
  const KLAVIYO_COUPON_POLL_INTERVAL_MS = 150;
  const KLAVIYO_COUPON_POLL_TIMEOUT_MS = 12_000;
  const RETRY_INTERVAL_MS = 400;
  const MAX_RENDER_RETRIES = 10;
  let previewPromise = null;
  let klaviyoCouponPollToken = 0;
  let klaviyoSubmitStartedAt = 0;

  function handoffNow() {
    return window.performance?.now?.() ?? Date.now();
  }

  function getRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function rerenderAll() {
    document.querySelectorAll('welcome-discount-price').forEach((node) => {
      if (typeof node.scheduleRender === 'function') node.scheduleRender();
    });
  }

  function markWelcomeClaimed() {
    try {
      localStorage.setItem(WELCOME_CLAIMED_KEY, '1');
    } catch {
      /* The discount handoff still works when persistent storage is unavailable. */
    }
    document.documentElement?.classList?.add('lelafine-welcome-claimed');
    document.documentElement?.classList?.add('lelafine-welcome-prepaint-hidden');
    window.dispatchEvent(new CustomEvent(WELCOME_CLAIMED_EVENT));
  }

  function stampHandoffTiming(stage) {
    if (!klaviyoSubmitStartedAt) return;
    if (!document.documentElement?.dataset) return;
    const elapsedMs = Math.round(handoffNow() - klaviyoSubmitStartedAt);
    document.documentElement.dataset.pkmWelcomeHandoffStage = stage;
    document.documentElement.dataset.pkmWelcomeHandoffMs = String(elapsedMs);
    if (stage === 'coupon-received' && !document.documentElement.dataset.pkmWelcomeHandoffCouponMs) {
      document.documentElement.dataset.pkmWelcomeHandoffCouponMs = String(elapsedMs);
    }
    if (stage === 'pdp-price-rendered' && !document.documentElement.dataset.pkmWelcomeHandoffPdpMs) {
      document.documentElement.dataset.pkmWelcomeHandoffPdpMs = String(elapsedMs);
    }
    if (stage === 'cart-price-rendered' && !document.documentElement.dataset.pkmWelcomeHandoffCartMs) {
      document.documentElement.dataset.pkmWelcomeHandoffCartMs = String(elapsedMs);
    }
  }

  function getDiscountCookieCode() {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/i);
    if (!cookieMatch?.[1]) return '';

    try {
      return decodeURIComponent(cookieMatch[1]).trim();
    } catch {
      return cookieMatch[1].trim();
    }
  }

  function isPlausibleKlaviyoCouponCode(value) {
    const code = String(value || '').trim();
    if (!code || code.length < 4 || code.length > 64 || /\s/.test(code)) return false;
    if (/^(loading|coupon|code|copied|applied)/i.test(code)) return false;
    return /^[a-z0-9_-]+$/i.test(code);
  }

  function getVisibleKlaviyoCouponCode() {
    const couponButtons = document.querySelectorAll('[aria-label="Copy coupon code"]');

    for (const button of couponButtons) {
      const rect = button.getBoundingClientRect();
      const style = getComputedStyle(button);
      const rendered = rect.width > 0
        && rect.height > 0
        && style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number(style.opacity || 1) > 0;
      if (!rendered) continue;

      const code = button.textContent?.trim() || '';
      if (isPlausibleKlaviyoCouponCode(code)) return code;
    }

    return '';
  }

  async function persistDynamicKlaviyoCoupon(code, { activateShopify = true } = {}) {
    const normalizedCode = String(code || '').trim().toLowerCase();
    if (!isPlausibleKlaviyoCouponCode(normalizedCode)) return false;
    // A Klaviyo welcome code never replaces BF access, neither in storage nor through /discount/.
    if (window.bfInsider?.isActive?.() && !window.bfInsider.isCode(normalizedCode)) return false;

    // Klaviyo's current Shopify coupon component already fetches this endpoint when the unique
    // code arrives. Repeat the same idempotent, same-origin call when the DOM fallback wins the
    // race, so Shopify owns the discount cookie rather than theme code manufacturing one. Do not
    // block the PDP price preview on the redirect: cart validation below remains authoritative.
    const activationRequest = activateShopify
      ? fetch(`${getRoot()}discount/${encodeURIComponent(normalizedCode)}`, {
          credentials: 'same-origin',
          redirect: 'follow',
        }).catch(() => {
        // The unique code is still safe to persist. Existing price preview/cart validation remains
        // fail-closed and will not show a reduced price if Shopify rejects the code.
        })
      : Promise.resolve();

    const previousCode = localStorage.getItem(CART_DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';
    localStorage.setItem(CART_DISCOUNT_CODE_KEY, normalizedCode);
    markWelcomeClaimed();
    stampHandoffTiming('coupon-received');
    if (previousCode !== normalizedCode) {
      localStorage.removeItem(CACHED_PERCENT_KEY);
      localStorage.removeItem(CACHED_CODE_KEY);
    }

    // Start the price proof immediately. Previously this waited for the discount redirect, a fixed
    // delay, cart application and a second reconcile before the PDP was even allowed to validate
    // the code. On an empty mobile cart that made a successful signup look idle for 5-6 seconds.
    window.dispatchEvent(new CustomEvent(CODE_CHANGED_EVENT));
    rerenderAll();

    // Let the scheduled component render claim the shared preview promise first, then finish the
    // durable cart/cookie reconciliation without racing its temporary empty-cart validation item.
    window.setTimeout(async () => {
      await activationRequest;
      if (previewPromise) await previewPromise;
      await activateWelcomeDiscountFromKlaviyo();
    }, 0);
    return true;
  }

  function startDynamicKlaviyoCouponHandoff() {
    klaviyoSubmitStartedAt = handoffNow();
    if (document.documentElement?.dataset) {
      delete document.documentElement.dataset.pkmWelcomeHandoffCouponMs;
      delete document.documentElement.dataset.pkmWelcomeHandoffPdpMs;
      delete document.documentElement.dataset.pkmWelcomeHandoffCartMs;
    }
    stampHandoffTiming('submit');
    const token = ++klaviyoCouponPollToken;
    const startedAt = Date.now();
    const initialCookieCode = getDiscountCookieCode().toLowerCase();
    const initialStoredCode = localStorage.getItem(CART_DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';

    const poll = async () => {
      if (token !== klaviyoCouponPollToken) return;

      const visibleCode = getVisibleKlaviyoCouponCode();
      const cookieCode = getDiscountCookieCode();
      const normalizedCookieCode = cookieCode.toLowerCase();
      const cookieCanHydrateStorage = normalizedCookieCode
        && (normalizedCookieCode !== initialCookieCode || !initialStoredCode);
      const code = visibleCode || (cookieCanHydrateStorage ? cookieCode : '');
      if (code && await persistDynamicKlaviyoCoupon(code, { activateShopify: Boolean(visibleCode) })) return;

      if (Date.now() - startedAt < KLAVIYO_COUPON_POLL_TIMEOUT_MS) {
        window.setTimeout(poll, KLAVIYO_COUPON_POLL_INTERVAL_MS);
      }
    };

    poll();
  }

  async function activateWelcomeDiscountFromKlaviyo() {
    window.__discountPreviewFailed = new Set();
    window.__discountPreviewKey = '';

    const apply = window.applyStoredDiscountToCart;
    if (typeof apply === 'function') {
      try {
        await apply({ refresh: true });
      } catch {
        /* preview/reconcile below may still recover */
      }
    }

    const reconcile = window.reconcileCartDiscountState;
    if (typeof reconcile === 'function') {
      try {
        await reconcile();
      } catch {
        /* ignore */
      }
    }

    window.dispatchEvent(new CustomEvent(CODE_CHANGED_EVENT));
    rerenderAll();
    window.setTimeout(rerenderAll, 600);
    window.setTimeout(rerenderAll, 1500);
  }

  function getPercentageFromCart(cart, code = '') {
    const normalizedCode = String(code || '').trim().toLowerCase();
    const applications = [];
    const addApplication = (entry) => {
      const application = entry?.discount_application || entry;
      if (application?.value_type === 'percentage') applications.push(application);
    };

    for (const entry of cart?.cart_level_discount_applications || []) addApplication(entry);
    for (const entry of cart?.discount_applications || []) addApplication(entry);
    for (const item of cart?.items || []) {
      for (const entry of item.line_level_discount_allocations || []) addApplication(entry);
    }

    if (normalizedCode) {
      const exactCodeApplication = applications.find((application) => (
        application.type === 'discount_code'
        && String(application.title || '').trim().toLowerCase() === normalizedCode
      ));
      if (exactCodeApplication) return exactCodeApplication;
    }

    return applications.find((application) => application.type === 'discount_code')
      || applications[0]
      || null;
  }

  function getValidatedDiscountPercent(cart, code = '') {
    const application = getPercentageFromCart(cart, code);
    const percent = application ? Number(application.value) : 0;
    return percent > 0 && percent <= 90 ? percent : 0;
  }

  if (!window.__welcomeDiscountKlaviyoCloseBound) {
    window.__welcomeDiscountKlaviyoCloseBound = true;
    window.addEventListener('klaviyoForms', (event) => {
      const { type, formId } = event.detail || {};
      const marketFormId = KLAVIYO_FORM_IDS[window.location.hostname.toLowerCase()] || '';

      // Back-in-stock, embedded forms and another market's form may never activate a welcome code.
      if (!marketFormId || formId !== marketFormId) return;
      if (type === 'submit') startDynamicKlaviyoCouponHandoff();
    });
  }

  document.addEventListener('cart-drawer:updated', () => {
    window.setTimeout(rerenderAll, 50);
  });

  window.rerenderWelcomeDiscountPrices = rerenderAll;

  if (customElements.get('welcome-discount-price')) return;

  class WelcomeDiscountPrice extends HTMLElement {
    connectedCallback() {
      this.amountElement = this.querySelector('[data-welcome-discount-amount]');
      this.variantPrices = this.readVariantPrices();
      this._renderTimer = null;
      this._retryTimer = null;
      this._retries = 0;

      this._onVariantChange = (e) => this.scheduleRender(this.dataset.fixedVariant === 'true' ? undefined : e.detail?.variant);
      this._onCartChange = () => this.scheduleRender(undefined, 100);
      this._onCodeChanged = () => {
        window.__discountPreviewFailed = new Set();
        window.__discountPreviewKey = '';
        this.scheduleRender(undefined, 50);
      };
      this._onNativeStorage = (e) => {
        if (e.key === CART_DISCOUNT_CODE_KEY) this._onCodeChanged();
      };

      document.addEventListener('variant:change', this._onVariantChange);
      document.addEventListener('cart:change', this._onCartChange);
      document.addEventListener('cart:refresh', this._onCartChange);
      window.addEventListener(CODE_CHANGED_EVENT, this._onCodeChanged);
      window.addEventListener('storage', this._onNativeStorage);

      this.bootstrapRender();
    }

    async bootstrapRender() {
      const reconcile = window.reconcileCartDiscountState;
      if (typeof reconcile === 'function') {
        try {
          await reconcile();
        } catch {
          /* ignore */
        }
      }
      this.scheduleRender();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
      document.removeEventListener('cart:change', this._onCartChange);
      document.removeEventListener('cart:refresh', this._onCartChange);
      window.removeEventListener(CODE_CHANGED_EVENT, this._onCodeChanged);
      window.removeEventListener('storage', this._onNativeStorage);
      clearTimeout(this._renderTimer);
      clearTimeout(this._retryTimer);
    }

    scheduleRender(variant, delay = 0) {
      clearTimeout(this._renderTimer);
      this._renderTimer = setTimeout(() => {
        this.render(variant);
        this.scheduleRetry(variant);
      }, delay);
    }

    scheduleRetry(variant) {
      if (!this.getStoredCode() || !this.hidden) {
        this._retries = 0;
        return;
      }
      if (this._retries >= MAX_RENDER_RETRIES) return;
      clearTimeout(this._retryTimer);
      this._retryTimer = setTimeout(() => {
        this._retries += 1;
        this.render(variant);
        this.scheduleRetry(variant);
      }, RETRY_INTERVAL_MS);
    }

    getStoredCode() {
      // Black Friday: the welcome discount is off for the whole campaign (bf-insider-head.liquid).
      if (window.__bfWelcomeOff) return '';
      const localCode = localStorage.getItem(CART_DISCOUNT_CODE_KEY)?.trim().toLowerCase() || '';
      const cookieMatch = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/i);
      const code = localCode || cookieMatch?.[1]?.trim().toLowerCase() || '';
      // A Black Friday insider code is shown by <bf-insider-price> (bf-insider-price.js); this
      // element then has no code of its own, so it neither previews it nor renders a pill.
      if (code && window.bfInsider?.isCode(code)) return '';
      return code;
    }

    getCachedPercent() {
      const storedCode = this.getStoredCode();
      if (!storedCode) return 0;

      const cachedCode = localStorage.getItem(CACHED_CODE_KEY)?.trim().toLowerCase();
      const cachedPercent = Number(localStorage.getItem(CACHED_PERCENT_KEY));

      if (!cachedCode || !cachedPercent || cachedPercent <= 0 || cachedPercent > 90) return 0;
      if (cachedCode !== storedCode) return 0;

      return cachedPercent;
    }

    cachePercent(percent, code) {
      if (!percent || !code) return;
      localStorage.setItem(CACHED_PERCENT_KEY, String(percent));
      localStorage.setItem(CACHED_CODE_KEY, code);
    }

    clearCache() {
      localStorage.removeItem(CACHED_PERCENT_KEY);
      localStorage.removeItem(CACHED_CODE_KEY);
    }

    getDiscountPercent() {
      const code = this.getStoredCode();
      if (!code) {
        this.clearCache();
        return 0;
      }
      return this.getCachedPercent();
    }

    shouldRequestPreview(variant) {
      const code = this.getStoredCode();
      const variantId = variant?.id || this.dataset.selectedVariantId;
      if (!code || !variantId || !this.getVariantSalePrice(variant)) return false;
      if (this.getDiscountPercent() > 0) return false;

      const key = `${code}:${variantId}`;
      window.__discountPreviewFailed ||= new Set();
      if (window.__discountPreviewFailed.has(key)) return false;
      if (window.__discountPreviewKey === key && this.getCachedPercent() > 0) return false;

      return true;
    }

    async runPreview(code, variantId) {
      const root = getRoot();
      const previewKey = `${code}:${variantId}`;
      window.__discountPreviewInProgress = true;
      window.__discountPreviewFailed ||= new Set();

      try {
        const cart = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        let result;

        if (cart.item_count > 0) {
          let validatedCart = cart;
          const codeOnCart = (validatedCart.discount_codes || []).some(
            (d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable !== false
          );

          if (!codeOnCart) {
            // Use the same serialized replacement/rollback as the drawer.
            // Two independent code writers can otherwise undo one another.
            if (typeof window.applyStoredDiscountToCart !== 'function') return;
            if (!await window.applyStoredDiscountToCart({ refresh: false, code })) {
              window.__discountPreviewFailed.add(previewKey);
              return;
            }
            validatedCart = await fetch(`${root}cart.js`, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            }).then((r) => r.json());

            const applied = (validatedCart.discount_codes || []).some(
              (d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable !== false
            );
            if (!applied) {
              window.__discountPreviewFailed.add(previewKey);
              return;
            }
          }

          const percent = getValidatedDiscountPercent(validatedCart, code);

          if (percent > 0 && percent <= 90) {
            this.cachePercent(percent, code);
            window.__discountPreviewKey = previewKey;
          } else {
            window.__discountPreviewFailed.add(previewKey);
          }
          return;
        } else {
          const addRes = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });

          if (!addRes.ok) {
            window.__discountPreviewFailed.add(previewKey);
            return;
          }

          result = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code }),
          }).then((r) => r.json());

          const previewItem = Array.isArray(result.items)
            ? result.items.find((item) => String(item.variant_id) === String(variantId))
            : null;

          // Shopify has now returned the authoritative discount application for the exact dynamic
          // code. Commit that proven percentage before waiting for the temporary empty-cart item to
          // be removed: mobile Safari otherwise makes the shopper stare at the old PDP price while
          // a cleanup request that cannot change the proof is still in flight.
          const percent = getValidatedDiscountPercent(result, code);
          if (percent) {
            this.cachePercent(percent, code);
            window.__discountPreviewKey = previewKey;
            rerenderAll();
          } else {
            window.__discountPreviewFailed.add(previewKey);
          }

          if (previewItem) {
            await fetch(`${root}cart/change.js`, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: previewItem.key, quantity: 0 }),
            });
          }

          return;
        }
      } catch {
        window.__discountPreviewFailed.add(previewKey);
      } finally {
        window.__discountPreviewInProgress = false;
      }
    }

    requestPreview(variant) {
      if (!this.shouldRequestPreview(variant)) return;

      const code = this.getStoredCode();
      const variantId = variant?.id || this.dataset.selectedVariantId;
      if (!code || !variantId || previewPromise) return;

      previewPromise = this.runPreview(code, variantId).finally(() => {
        previewPromise = null;
        rerenderAll();
      });
    }

    getVariantEntry(variant) {
      const variantId = String(variant?.id || this.dataset.selectedVariantId);
      return this.variantPrices[variantId] || null;
    }

    getVariantSalePrice(variant) {
      if (variant?.price) return Number(variant.price);
      const entry = this.getVariantEntry(variant);
      if (entry) return entry.price;
      return Number(this.dataset.selectedVariantPrice) || 0;
    }

    getVariantCompareAtPrice(variant) {
      if (variant?.compare_at_price) return Number(variant.compare_at_price);
      const entry = this.getVariantEntry(variant);
      if (entry?.compareAt) return entry.compareAt;
      return Number(this.dataset.selectedVariantCompareAt) || 0;
    }

    getBasePrice(variant) {
      const salePrice = this.getVariantSalePrice(variant);
      const compareAt = this.getVariantCompareAtPrice(variant);
      if (this.dataset.discountBase === 'sale') return salePrice;
      if (compareAt > salePrice) return compareAt;
      return salePrice;
    }

    readVariantPrices() {
      const script = this.querySelector('[data-welcome-discount-variants]');
      try {
        return JSON.parse(script?.textContent || '[]').reduce((acc, v) => {
          acc[String(v.id)] = {
            price: Number(v.price),
            compareAt: v.compare_at_price ? Number(v.compare_at_price) : 0,
          };
          return acc;
        }, {});
      } catch {
        return {};
      }
    }

    render(variant) {
      const percent = this.getDiscountPercent();
      const basePrice = this.getBasePrice(variant);
      // Only the main PDP block uses the wrapper for show/hide — nested instances in
      // gift-wrap / complementary rows share the parent block item and must not hide it.
      const wrapper = this.closest('.product-info__block-item[data-block-type="welcome-discount-price"]');

      if (!percent || !basePrice || !this.amountElement) {
        this.hidden = true;
        // The wrapper also holds the BF insider price, which keeps it open while it shows.
        if (wrapper && !wrapper.querySelector('bf-insider-price:not([hidden])')) wrapper.hidden = true;
        if (this.getStoredCode() && this.getVariantSalePrice(variant)) this.requestPreview(variant);
        return;
      }

      const discountedPrice = Math.round((basePrice * (100 - percent)) / 100);
      const formatted = this.formatMoney(discountedPrice);
      const hasVisibleLabel = Boolean(this.querySelector('[data-welcome-discount-label]'));
      this.amountElement.textContent = hasVisibleLabel ? ` ${formatted}` : formatted;

      // PDP-top full display: the pill is the whole state-B price. Render the
      // struck anchor (compare-at, else the original price) beside it and the
      // savings (anchor − final) on the second line.
      if (this.dataset.fullDisplay === 'true') {
        const anchorElement = this.querySelector('[data-welcome-discount-anchor]');
        const savingsElement = this.querySelector('[data-welcome-discount-savings]');
        const salePrice = this.getVariantSalePrice(variant);
        const compareAt = this.getVariantCompareAtPrice(variant);
        const anchor = compareAt > salePrice ? compareAt : salePrice;

        if (anchor > discountedPrice) {
          if (anchorElement) {
            anchorElement.textContent = this.formatMoney(anchor);
            anchorElement.hidden = false;
          }
          if (savingsElement) {
            // DE renders a fixed "code applied" badge with its own icon and markup, so it is only
            // shown or hidden here — writing textContent would wipe it. See the snippet's comment.
            if (this.dataset.savingsStatic !== 'true') {
              const savingsLabel = this.dataset.savingsLabel || '';
              const savingsAmount = this.formatMoney(anchor - discountedPrice);
              savingsElement.textContent = savingsLabel ? `${savingsLabel} ${savingsAmount}` : savingsAmount;
            }
            savingsElement.hidden = false;
          }
        } else {
          if (anchorElement) anchorElement.hidden = true;
          if (savingsElement) savingsElement.hidden = true;
        }
      }

      this.hidden = false;
      if (wrapper) wrapper.hidden = false;
      stampHandoffTiming(this.closest('cart-drawer') ? 'cart-price-rendered' : 'pdp-price-rendered');
    }

    formatMoney(cents) {
      const locale = this.dataset.locale || document.documentElement.lang || 'da-DK';
      const currency = this.dataset.currency || 'DKK';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol',
        }).format(cents / 100);
      } catch {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }
  }

  customElements.define('welcome-discount-price', WelcomeDiscountPrice);
})();
